import React, { FC, useEffect, useRef, useState } from 'react';
import { Animated, Image, View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { assets } from '@assets';
import { TWCTimeLogoIDs } from '@constants';
import { currentLocationWeatherState, dayDateTimeState } from '@store';

import { styles } from './styles';

import { TWCText } from '../../../primitives/TWCText/TWCText';

/**
 * TWCTimeLogoAnimated: A reusable component for displaying the weather channel logo and current system time. Can take in an override
 * for the logo url. Now with animation!
 */

export const TWCTimeLogoAnimatedTestIds = {
  container: 'twc-time-logo-animated-container',
  temperature: 'twc-time-logo-animated-temperature',
  time: 'twc-time-logo-animated-time',
};

export type TWCTimeLogoAnimatedProps = {
  logo?: string;
};

export const TWCTimeLogoAnimated: FC<TWCTimeLogoAnimatedProps> = ({ logo }) => {
  const { time } = useRecoilValue(dayDateTimeState);
  const { data: currentLocationWeatherData } = useRecoilValue(currentLocationWeatherState);

  const temperatureFadeAnim = useRef(new Animated.Value(0)).current;
  const timeFadeAnim = useRef(new Animated.Value(1)).current;

  const [resetAnimation, setResetAnimation] = useState(false);

  const temperatureFadeIn = () => {
    Animated.timing(temperatureFadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: false,
    }).start();
  };

  const temperatureFadeOut = () => {
    Animated.timing(temperatureFadeAnim, {
      toValue: 0,
      duration: 500,
      useNativeDriver: false,
    }).start();
  };

  const timeFadeIn = () => {
    Animated.timing(timeFadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: false,
    }).start();
  };

  const timeFadeOut = () => {
    Animated.timing(timeFadeAnim, {
      toValue: 0,
      duration: 500,
      useNativeDriver: false,
    }).start();
  };

  const firstAnimation = () => {
    timeFadeOut();
    temperatureFadeIn();
  };

  const secondAnimation = () => {
    timeFadeIn();
    temperatureFadeOut();
  };

  useEffect(() => {
    const firstTimeout = setTimeout(() => {
      firstAnimation();
    }, 10000);

    const secondTimeout = setTimeout(() => {
      secondAnimation();
      clearTimeout(firstTimeout);

      setResetAnimation(!resetAnimation);
    }, 20000);

    return () => {
      clearTimeout(firstTimeout);
      clearTimeout(secondTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAnimation]);

  const sourceIcon = logo ? { uri: logo } : assets.twcSmallLogo;

  return (
    <View style={styles.logoTime} testID={TWCTimeLogoAnimatedTestIds.container}>
      <Image source={sourceIcon} style={styles.logo} testID={TWCTimeLogoIDs.logoAndTimeWidgetLogo} />
      <View style={styles.timeBackground}>
        <Animated.View
          testID={TWCTimeLogoAnimatedTestIds.temperature}
          style={[styles.temperatureTimeWrapper, { opacity: temperatureFadeAnim }]}
        >
          <TWCText testID={TWCTimeLogoIDs.logoAndTimeWidgetTime} variant="mediumCondensed">
            {typeof currentLocationWeatherData?.temperature === 'number'
              ? `${currentLocationWeatherData.temperature}°`
              : '-'}
          </TWCText>
        </Animated.View>
        <Animated.View
          testID={TWCTimeLogoAnimatedTestIds.time}
          style={[styles.temperatureTimeWrapper, { opacity: timeFadeAnim }]}
        >
          <TWCText testID={TWCTimeLogoIDs.logoAndTimeWidgetTime} variant="mediumCondensed">
            {time ?? '-'}
          </TWCText>
        </Animated.View>
      </View>
    </View>
  );
};
